export interface SeatLayout {
  rows: SeatRow[];
  totalSeats: number;
  busType: string;
  seatsPerRow: number;
  aisleAfter?: number; // For 2-2 layouts, aisle after seat 2
}

export interface SeatRow {
  rowNumber: number;
  seats: Seat[];
  isLastRow?: boolean;
  /**
   * Full visual geometry of the row, left-to-right across a fixed column grid.
   * Renderers iterate this; `seats` stays as the seats-only list.
   */
  cells: SeatCell[];
}

export interface Seat {
  number: string;
  position: 'left' | 'right' | 'center';
  isAisle?: boolean;
}

/**
 * One slot in a rendered row. Coaches use a 5-column model:
 *   col 0 = L1, col 1 = L2, col 2 = aisle (or lone center seat),
 *   col 3 = R1, col 4 = R2.
 * `toilet` may span columns/rows; `toilet-cont` marks the slots it covers.
 */
export type SeatCell =
  | { type: 'seat'; col: number; number: string; position: 'left' | 'right' | 'center' }
  | { type: 'aisle'; col: number }
  | { type: 'empty'; col: number }
  | { type: 'toilet'; col: number; colSpan: number; rowSpan: number }
  | { type: 'toilet-cont'; col: number };

// Column indices for the 5-wide coach grid.
const COL_L1 = 0;
const COL_L2 = 1;
const COL_AISLE = 2;
const COL_R1 = 3;
const COL_R2 = 4;

/**
 * Token grid describing the irregular rear block of a real Van Hool coach.
 *   s  = seat (left/right inferred from column)
 *   c  = lone seat in the aisle/center column
 *   .  = empty slot
 *   W2 = toilet box spanning 2 columns (R1+R2) and 2 rows (this row + next)
 *   W1 = toilet box spanning 2 columns (R1+R2) and 1 row
 *   x  = continuation slot covered by a spanning toilet above (renders nothing)
 */
type RearToken = 's' | 'c' | '.' | 'W2' | 'W1' | 'x';

interface VanHoolTemplate {
  fullRows: number; // count of standard 2-2 rows up front
  rear: RearToken[][]; // each inner array is one rear row, length up to 5
}

// The five physical Van Hool layouts in the fleet, keyed by seat capacity.
// Derived from the manufacturer seat charts (screenshots/{53,56,57actual,59,61}.png).
const VAN_HOOL_LAYOUTS: Record<number, VanHoolTemplate> = {
  // 48 + {49,50} + {51,52,53}
  53: {
    fullRows: 12,
    rear: [
      ['s', 's', '.', 'W2', 'x'],
      ['s', 's', 'c', 'x', 'x'],
    ],
  },
  // 52 + {53,54} + {55,56}
  56: {
    fullRows: 13,
    rear: [
      ['s', 's', '.', 'W2', 'x'],
      ['s', 's', '.', 'x', 'x'],
    ],
  },
  // 52 + {53,54} + {55,56,57}
  57: {
    fullRows: 13,
    rear: [
      ['s', 's', '.', 'W2', 'x'],
      ['s', 's', 'c', 'x', 'x'],
    ],
  },
  // 56 + {57,58,59}
  59: {
    fullRows: 14,
    rear: [['s', 's', 'c', 'W1', 'x']],
  },
  // 56 + {57,58} + {59,60,61}
  61: {
    fullRows: 14,
    rear: [
      ['s', 's', '.', 'W2', 'x'],
      ['s', 's', 'c', 'x', 'x'],
    ],
  },
};

// Builds the 5-cell geometry for a standard 2-2 coach row.
const buildFullRowCells = (left: string[], right: string[]): SeatCell[] => {
  const cells: SeatCell[] = [];
  if (left[0]) cells.push({ type: 'seat', col: COL_L1, number: left[0], position: 'left' });
  if (left[1]) cells.push({ type: 'seat', col: COL_L2, number: left[1], position: 'left' });
  cells.push({ type: 'aisle', col: COL_AISLE });
  if (right[0]) cells.push({ type: 'seat', col: COL_R1, number: right[0], position: 'right' });
  if (right[1]) cells.push({ type: 'seat', col: COL_R2, number: right[1], position: 'right' });
  return cells;
};

// Builds a coach layout from a Van Hool template, auto-numbering seats 1..N.
const buildVanHoolLayout = (totalSeats: number, tpl: VanHoolTemplate): SeatLayout => {
  const rows: SeatRow[] = [];
  let seatNumber = 1;
  let rowNumber = 1;

  // Front: standard 2-2 rows.
  for (let r = 0; r < tpl.fullRows; r++) {
    const left = [String(seatNumber++), String(seatNumber++)];
    const right = [String(seatNumber++), String(seatNumber++)];
    const cells = buildFullRowCells(left, right);
    rows.push({
      rowNumber,
      seats: cells.filter((c): c is Extract<SeatCell, { type: 'seat' }> => c.type === 'seat')
        .map((c) => ({ number: c.number, position: c.position })),
      cells,
    });
    rowNumber++;
  }

  // Rear: irregular block from the template tokens, filled in column order.
  tpl.rear.forEach((tokenRow, idx) => {
    const cells: SeatCell[] = [];
    tokenRow.forEach((token, col) => {
      switch (token) {
        case 's': {
          const position = col <= COL_L2 ? 'left' : 'right';
          cells.push({ type: 'seat', col, number: String(seatNumber++), position });
          break;
        }
        case 'c':
          cells.push({ type: 'seat', col, number: String(seatNumber++), position: 'center' });
          break;
        case 'W2':
          cells.push({ type: 'toilet', col, colSpan: 2, rowSpan: 2 });
          break;
        case 'W1':
          cells.push({ type: 'toilet', col, colSpan: 2, rowSpan: 1 });
          break;
        case 'x':
          cells.push({ type: 'toilet-cont', col });
          break;
        case '.':
          if (col === COL_AISLE) cells.push({ type: 'aisle', col });
          else cells.push({ type: 'empty', col });
          break;
      }
    });
    rows.push({
      rowNumber,
      seats: cells.filter((c): c is Extract<SeatCell, { type: 'seat' }> => c.type === 'seat')
        .map((c) => ({ number: c.number, position: c.position })),
      cells,
      isLastRow: idx === tpl.rear.length - 1,
    });
    rowNumber++;
  });

  return {
    rows,
    totalSeats,
    busType: 'coach-bus',
    seatsPerRow: 4,
    aisleAfter: 2,
  };
};

/**
 * Generate a seat layout based on total seats.
 * RutaLink uses sequential numeric seat numbers (1, 2, 3...) NOT airline-style (1A, 1B).
 */
export const getSeatLayout = (totalSeats: number = 57): SeatLayout => {
  // Known physical Van Hool layouts: render the real floor plan + toilet.
  const template = VAN_HOOL_LAYOUTS[totalSeats];
  if (template) {
    return buildVanHoolLayout(totalSeats, template);
  }

  // Generic fallback for any other capacity: uniform grid, no toilet.
  const rows: SeatRow[] = [];
  let seatNumber = 1;
  let rowNumber = 1;

  // Determine seats per row based on total capacity
  const seatsPerRow = totalSeats <= 25 ? 3 : 4;
  const busType = totalSeats <= 25 ? 'mini-bus' : totalSeats <= 45 ? 'standard-bus' : 'coach-bus';

  while (seatNumber <= totalSeats) {
    const seatsInThisRow = Math.min(seatsPerRow, totalSeats - seatNumber + 1);
    const seats: Seat[] = [];
    const cells: SeatCell[] = [];

    for (let i = 0; i < seatsInThisRow; i++) {
      let position: 'left' | 'right' | 'center';
      let col: number;

      if (seatsPerRow === 3) {
        // Mini-bus: 1-2 configuration
        position = i === 0 ? 'left' : i === 1 ? 'center' : 'right';
        col = i === 0 ? COL_L1 : i === 1 ? COL_AISLE : i === 2 ? COL_R1 : COL_R2;
      } else {
        // Standard/Coach: 2-2 configuration
        position = i <= 1 ? 'left' : 'right';
        col = i === 0 ? COL_L1 : i === 1 ? COL_L2 : i === 2 ? COL_R1 : COL_R2;
      }

      const number = seatNumber.toString();
      seats.push({ number, position });
      cells.push({ type: 'seat', col, number, position });

      seatNumber++;
    }

    // Insert the aisle marker between the left block and the right block.
    if (!cells.some((c) => c.col === COL_AISLE)) {
      cells.push({ type: 'aisle', col: COL_AISLE });
    }
    cells.sort((a, b) => a.col - b.col);

    rows.push({
      rowNumber,
      seats,
      cells,
      isLastRow: seatNumber > totalSeats
    });

    rowNumber++;
  }

  return {
    rows,
    totalSeats,
    busType,
    seatsPerRow,
    aisleAfter: seatsPerRow === 4 ? 2 : undefined
  };
};

export type SeatStatus = 'available' | 'occupied' | 'held' | 'selected';

export const getSeatStatusClass = (status: SeatStatus): string => {
  switch (status) {
    case 'occupied':
      return 'bg-muted text-muted-foreground cursor-not-allowed';
    case 'held':
      return 'bg-yellow-200 text-yellow-800 cursor-not-allowed';
    case 'selected':
      return 'bg-primary text-primary-foreground';
    default:
      return 'bg-green-100 text-green-800 hover:bg-green-200 cursor-pointer';
  }
};
